import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCubes, 
  faExchangeAlt, 
  faProjectDiagram, 
  faLayerGroup,
  faChartLine,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/ServicePage.css';

const BimPage: React.FC = () => {
  return (
    <div className="service-page">
      {/* Header Section */}
      <header className="service-header">
        <div className="container">
          <div className="service-header-content">
            <div className="service-icon">
              <FontAwesomeIcon icon={faCubes} />
            </div>
            <h1>Building Information Modeling (BIM)</h1>
            <p>
              Moderne Planungsmethoden für effiziente und transparente Bauprojekte
            </p>
          </div>
        </div>
      </header>

      <div className="service-content">
        <div className="container">
          {/* Introduction Section */}
          <section className="service-section">
            <h2 className="service-section-title">BIM - Die Zukunft der Bauplanung</h2>
            <p>
              Building Information Modeling (BIM) ist eine innovative Methode für die Planung, Ausführung und 
              Bewirtschaftung von Gebäuden. Das Ingenieurbüro Neubaur setzt auf diese moderne Technologie, 
              um Bauprojekte effizienter, transparenter und nachhaltiger zu gestalten.
            </p>
            <p>
              Mit BIM erstellen wir digitale 3D-Modelle, die nicht nur geometrische Informationen, sondern auch 
              umfassende Daten zu allen Bauteilen und -komponenten enthalten. Dies ermöglicht eine ganzheitliche 
              Betrachtung des Gebäudes über seinen gesamten Lebenszyklus hinweg.
            </p>
          </section>

          {/* Features Section */}
          <section className="service-section">
            <h2 className="service-section-title">Unsere Leistungen im Bereich BIM</h2>
            <div className="service-features">
              <div className="service-feature">
                <div className="service-feature-icon">
                  <FontAwesomeIcon icon={faCubes} />
                </div>
                <h3 className="service-feature-title">3D-Modellierung</h3>
                <p className="service-feature-text">
                  Erstellung detaillierter 3D-Modelle mit umfassenden Bauteilinformationen.
                </p>
              </div>
              
              <div className="service-feature">
                <div className="service-feature-icon">
                  <FontAwesomeIcon icon={faExchangeAlt} />
                </div>
                <h3 className="service-feature-title">Kollisionsprüfung</h3>
                <p className="service-feature-text">
                  Frühzeitige Erkennung von Kollisionen zwischen verschiedenen Gewerken durch digitale Modellprüfung.
                </p>
              </div>
              
              <div className="service-feature">
                <div className="service-feature-icon">
                  <FontAwesomeIcon icon={faProjectDiagram} />
                </div>
                <h3 className="service-feature-title">BIM-Koordination</h3>
                <p className="service-feature-text">
                  Koordination der verschiedenen Fachplanungen im BIM-Modell für eine optimale Abstimmung.
                </p>
              </div>
              
              <div className="service-feature">
                <div className="service-feature-icon">
                  <FontAwesomeIcon icon={faLayerGroup} />
                </div>
                <h3 className="service-feature-title">Datenmanagement</h3>
                <p className="service-feature-text">
                  Strukturierte Erfassung und Verwaltung aller relevanten Gebäudedaten im BIM-Modell.
                </p>
              </div>
              
              <div className="service-feature">
                <div className="service-feature-icon">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <h3 className="service-feature-title">Simulationen</h3>
                <p className="service-feature-text">
                  Durchführung von Simulationen für Energieverbrauch, Tageslicht, Raumklima und mehr.
                </p>
              </div>
              
              <div className="service-feature">
                <div className="service-feature-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <h3 className="service-feature-title">BIM-Schulungen</h3>
                <p className="service-feature-text">
                  Schulungen und Workshops für Bauherren und Projektbeteiligte zum Thema BIM.
                </p>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="service-section">
            <h2 className="service-section-title">Vorteile von BIM</h2>
            <div className="service-process">
              <div className="process-step">
                <div className="process-number">1</div>
                <div className="process-content">
                  <h3 className="process-title">Höhere Planungsqualität</h3>
                  <p className="process-text">
                    Durch die dreidimensionale Visualisierung und automatische Kollisionsprüfung werden Planungsfehler frühzeitig erkannt und vermieden.
                  </p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="process-number">2</div>
                <div className="process-content">
                  <h3 className="process-title">Bessere Zusammenarbeit</h3>
                  <p className="process-text">
                    Alle Projektbeteiligten arbeiten an einem gemeinsamen digitalen Modell, was die Kommunikation und Abstimmung verbessert.
                  </p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="process-number">3</div>
                <div className="process-content">
                  <h3 className="process-title">Kostentransparenz</h3>
                  <p className="process-text">
                    Durch die detaillierte Erfassung aller Bauteile und -komponenten können Kosten präziser kalkuliert und kontrolliert werden.
                  </p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="process-number">4</div>
                <div className="process-content">
                  <h3 className="process-title">Zeitersparnis</h3>
                  <p className="process-text">
                    Durch die parallele Bearbeitung und automatisierte Prozesse wird die Planungszeit verkürzt.
                  </p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="process-number">5</div>
                <div className="process-content">
                  <h3 className="process-title">Nachhaltigkeit</h3>
                  <p className="process-text">
                    Durch Simulationen können energetische Aspekte bereits in der Planungsphase optimiert werden.
                  </p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="process-number">6</div>
                <div className="process-content">
                  <h3 className="process-title">Lebenszyklusbetrachtung</h3>
                  <p className="process-text">
                    Das BIM-Modell kann auch nach Fertigstellung des Gebäudes für Betrieb und Wartung genutzt werden.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="service-cta">
            <h2 className="service-cta-title">Die Zukunft der Bauplanung für Ihr Projekt</h2>
            <p className="service-cta-text">
              Nutzen Sie die Vorteile von BIM für Ihr nächstes Bauprojekt. 
              Wir beraten Sie gerne zu den Möglichkeiten und unterstützen Sie bei der Implementierung.
            </p>
            <Link to="/kontakt" className="btn btn-primary">
              Kontakt aufnehmen
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
};

export default BimPage;
